/**
 * FieldDefinition → OpenAPI 3.0 schema converter.
 *
 * Used by the spec builder to generate request bodies and response schemas
 * from entity field definitions.
 */

import type { FieldDefinition, InputType } from '@gzl10/nexus-sdk'

export interface OpenApiSchema {
  type: string
  format?: string
  enum?: string[]
  properties?: Record<string, OpenApiSchema>
  required?: string[]
  items?: OpenApiSchema
  additionalProperties?: boolean | OpenApiSchema
}

export interface FieldsSchemaOptions {
  excludeFields?: string[]
}

const DEFAULT_LOCALES = ['en', 'es'] as const

function baseSchemaForInput(input: InputType): OpenApiSchema {
  switch (input) {
    case 'text':
    case 'textarea':
    case 'markdown':
    case 'password':
    case 'color':
    case 'icon':
    case 'slug':
    case 'tel':
    case 'otp':
    case 'cron':
    case 'code':
    case 'hidden':
    case 'autocomplete':
    case 'radio':
    case 'select':
      return { type: 'string' }
    case 'email':
      return { type: 'string', format: 'email' }
    case 'url':
      return { type: 'string', format: 'uri' }
    case 'uuid':
      return { type: 'string', format: 'uuid' }
    case 'number':
    case 'decimal':
    case 'rate':
    case 'range':
      return { type: 'number' }
    case 'checkbox':
    case 'switch':
      return { type: 'boolean' }
    case 'date':
      return { type: 'string', format: 'date' }
    case 'datetime':
      return { type: 'string', format: 'date-time' }
    case 'time':
      return { type: 'string', format: 'time' }
    case 'json':
      return { type: 'object', additionalProperties: true }
    case 'file':
    case 'image':
      return { type: 'string', format: 'binary' }
    case 'multifile':
    case 'multiimage':
      return { type: 'array', items: { type: 'string', format: 'binary' } }
    case 'multiselect':
    case 'transfer':
    case 'tags':
      return { type: 'array', items: { type: 'string' } }
    default:
      return { type: 'string' }
  }
}

export function fieldToOpenApiSchema(field: FieldDefinition): OpenApiSchema {
  let schema: OpenApiSchema

  // db.type overrides (integer, uuid for relations)
  if (field.db?.type === 'integer') {
    schema = { type: 'integer' }
  } else if (field.db?.type === 'uuid') {
    schema = { type: 'string', format: 'uuid' }
  } else {
    schema = baseSchemaForInput(field.input)
  }

  // Enum from validation or static options (only applies to string-like scalars)
  if (schema.type === 'string') {
    const enumValues =
      field.validation?.enum ??
      (field.options?.static?.length ? field.options.static.map((o) => o.value) : undefined)
    if (enumValues && enumValues.length > 0) {
      schema = { ...schema, enum: enumValues }
    }
  }

  // Localized: wrap in object keyed by locale
  if (field.localized === true) {
    const locales: Record<string, OpenApiSchema> = {}
    for (const locale of DEFAULT_LOCALES) {
      locales[locale] = schema
    }
    schema = {
      type: 'object',
      properties: locales,
      additionalProperties: true,
    }
  }

  return schema
}

export function fieldsToOpenApiObject(
  fields: Record<string, FieldDefinition>,
  options: FieldsSchemaOptions = {}
): OpenApiSchema {
  const exclude = new Set(options.excludeFields ?? [])
  const properties: Record<string, OpenApiSchema> = {}
  const required: string[] = []

  for (const [key, def] of Object.entries(fields)) {
    if (exclude.has(key)) continue
    if (def.hidden === true) continue

    properties[key] = fieldToOpenApiSchema(def)
    if (def.required === true) required.push(key)
  }

  const result: OpenApiSchema = {
    type: 'object',
    properties,
  }
  if (required.length > 0) result.required = required
  return result
}
